import * as Constants from './Constants';
import AttachmentUrlType from 'owa-files-url/lib/schema/AttachmentUrlType';
import endImageLoadPerfDataPoint from './endImageLoadPerfDataPoint';
import { getPalette } from 'owa-theme';
import loadFromAttachmentServiceUrl from './loadFromAttachmentServiceUrl';
import getMailboxInfoFromImageElement from './getMailboxInfoFromImageElement';
import loadFromUrl from './loadFromUrl';
import logImageLoadError from './logImageLoadError';
import safeGetAttribute from './safeGetAttribute';
import startImageLoadPerfDataPoint from './startImageLoadPerfDataPoint';
import store from '../store/store';
import { getAttachmentUrlForClassic } from 'owa-files-url';
import {
    INLINEIMAGE_ATTRIBUTE_DATA_CUSTOM,
    INLINEIMAGE_ATTRIBUTE_ORIGINALSRC,
} from 'owa-inline-image-consts';

const ATTRIBUTE_NATURAL_WIDTH = 'naturalwidth';
const ATTRIBUTE_NATURAL_HEIGHT = 'naturalheight';
const ATTRIBUTE_WIDTH = 'width';
const ATTRIBUTE_HEIGHT = 'height';
const PIXEL_SUFFIX = 'px';

/**
 * Load an image from attachment
 * @param img The image
 * @param context the context where this load happens
 * @param usePlaceHolder should create place holder while loads happens
 */
export default function loadFromAttachment(
    img: HTMLImageElement,
    context: string,
    usePlaceHolder: boolean = true
): boolean {
    const dataCustom = safeGetAttribute(img, INLINEIMAGE_ATTRIBUTE_DATA_CUSTOM);
    const imageType = safeGetAttribute(img, Constants.ATTRIBUTE_IMAGETYPE);
    const originalSrc = safeGetAttribute(img, INLINEIMAGE_ATTRIBUTE_ORIGINALSRC);
    const cachedDataUri = originalSrc.length > 0 ? store.inlineImages[originalSrc] : null;
    if (dataCustom.length == 0 && !cachedDataUri) {
        // dataCustom is used for content from server
        // cachedDataUri is used for local lie
        return false;
    }

    // First check to see if we have a dataURI stored for this image
    if (cachedDataUri) {
        // This is local lie. We have a data uri version of the image in cache saved from compose (inline reply)
        // Instead of loading the image from attachment service which could flickering, we just use data uri in cache
        const loadDataPoint = startImageLoadPerfDataPoint();
        loadFromUrl(
            img,
            cachedDataUri,
            (imageElement: HTMLImageElement) => {
                fixImageDimension(imageElement);
                endImageLoadPerfDataPoint(
                    loadDataPoint,
                    imageType,
                    Constants.HOWLOAD_DATAURI,
                    '' /* error */,
                    context
                );
            },
            (imageElement: HTMLImageElement) => {
                endImageLoadPerfDataPoint(
                    loadDataPoint,
                    imageType,
                    Constants.HOWLOAD_DATAURI,
                    Constants.ERROR_ONERROR,
                    context
                );
                logImageLoadError(
                    imageType /* type */,
                    Constants.HOWLOAD_DATAURI /* howLoad */,
                    Constants.ERROR_ONERROR /* error */,
                    '' /* statusCode*/,
                    context /* context */,
                    '' /* loaderCode*/,
                    originalSrc /* originalSrc*/,
                    '' /* contentType */
                );
            }
        );
    } else {
        const attachmentId = {
            Id: decodeURIComponent(dataCustom),
            mailboxInfo: getMailboxInfoFromImageElement(img),
        };

        const previewUrl = getAttachmentUrlForClassic(
            attachmentId.mailboxInfo.routingKey,
            attachmentId,
            AttachmentUrlType.Preview
        );
        if (previewUrl != null && previewUrl.length > 0) {
            const originalDisplayStyle: string = img.style.display;

            // Create place holder as needed
            const placeholderDiv = usePlaceHolder ? addImageLoadingPlaceholder(img) : null;

            // CTQ logging is already done in loadFromAttachmentServiceUrl so no need to do it here again
            loadFromAttachmentServiceUrl(
                img,
                previewUrl,
                context,
                (imageElement: HTMLImageElement) => {
                    cleanupImageLoadingPlaceholder(placeholderDiv);
                    fixImageDimension(imageElement);
                    imageElement.style.display = originalDisplayStyle;
                },
                (imageElement: HTMLImageElement) => {
                    // No need to set src to be originalSrc. The purpose of setting originalSrc is to render a broken image so that users can visibly spot it
                    // In error callback, a broken image is expected so no need to do this again here
                    cleanupImageLoadingPlaceholder(placeholderDiv);
                    imageElement.style.display = 'inline';
                },
                true /* retryOnFailure */
            );
        } else {
            logImageLoadError(
                imageType /* type */,
                '' /* howLoad */,
                Constants.ERROR_NULL_ATTACHMENT_URL /* error */,
                '' /* statusCode*/,
                context /* context */,
                '' /* loaderCode*/,
                originalSrc /* originalSrc*/,
                '' /* contentType */
            );
            // Bug 51037: Remove src completely for inline image when it is not found
            // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/51037
            // Restoring src to originalSrc which in most cases is cid:xxxx will cause browser
            // to show a mixed content warning. To avoid the warning, just remove the src entirely
            // which will have same effect of showing a broken image
            img.removeAttribute('src');
        }
    }

    return true;
}

function cleanupImageLoadingPlaceholder(placeholderDiv: HTMLDivElement) {
    if (placeholderDiv?.parentNode) {
        placeholderDiv.parentNode.removeChild(placeholderDiv);
    }
}

function fixImageDimension(img: HTMLImageElement) {
    // Bug 37742: Don't mess around height and width when there is already a height and width post inline image load
    // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/37742
    // The max-width:100% is essentially to prevent a vertial scrollbar from appearing for large image, we found however
    // it can also change the dimension of image when there is already a width/height on the img
    const imgStyle = img.style;
    if (
        !img.hasAttribute('width') &&
        !imgStyle.width &&
        !img.hasAttribute('height') &&
        !imgStyle.height
    ) {
        imgStyle.maxWidth = '100%';
        imgStyle.height = 'auto';
    }
}

/**
 * Creates and adds an image loading placeholder for the given img element.
 * This will also add the placeholder div to the DOM and hide the img element
 * @param img The image
 */
function addImageLoadingPlaceholder(img: HTMLImageElement): HTMLDivElement {
    let placeholderWidth = '50px';
    let placeholderHeight = '50px';

    // Try to determine the width/height of the image. First check to see if there is an explicit width/height set in the image's styles.
    // Then check to see if the img element itself has a width & height, or lacking that get the naturalwidth and naturalheight
    if (img.style.width && img.style.height) {
        placeholderWidth = img.style.width;
        placeholderHeight = img.style.height;
    } else if (img.getAttribute(ATTRIBUTE_WIDTH) && img.getAttribute(ATTRIBUTE_HEIGHT)) {
        placeholderWidth = img.getAttribute(ATTRIBUTE_WIDTH) + PIXEL_SUFFIX;
        placeholderHeight = img.getAttribute(ATTRIBUTE_HEIGHT) + PIXEL_SUFFIX;
    } else if (
        img.getAttribute(ATTRIBUTE_NATURAL_WIDTH) &&
        img.getAttribute(ATTRIBUTE_NATURAL_HEIGHT)
    ) {
        placeholderWidth = img.getAttribute(ATTRIBUTE_NATURAL_WIDTH) + PIXEL_SUFFIX;
        placeholderHeight = img.getAttribute(ATTRIBUTE_NATURAL_HEIGHT) + PIXEL_SUFFIX;
    }

    // Create the placeholder div and style it
    const themeSymbols = getPalette();
    const placeholderDiv: HTMLDivElement = document.createElement('div');
    placeholderDiv.style.display = 'inline-block';
    placeholderDiv.style.width = placeholderWidth;
    placeholderDiv.style.height = placeholderHeight;
    placeholderDiv.style.maxWidth = '100%';
    placeholderDiv.style.maxHeight = img.style.maxHeight;
    placeholderDiv.style.borderWidth = '1px';
    placeholderDiv.style.borderStyle = 'dashed';
    placeholderDiv.style.borderColor = themeSymbols.themePrimary;
    placeholderDiv.style.boxSizing = 'border-box';
    img.style.display = 'none';
    img.parentNode.insertBefore(placeholderDiv, img);

    if (img.getAttribute(ATTRIBUTE_NATURAL_WIDTH) && img.getAttribute(ATTRIBUTE_NATURAL_HEIGHT)) {
        // Resize placeholderDiv proportion to img naturalwidth/naturalheight aspect ratio.
        const placeholderActualWidth = Math.min(
            parseInt(placeholderDiv.style.width),
            placeholderDiv.offsetWidth
        );
        placeholderDiv.style.height = Math.ceil(
            (placeholderActualWidth / parseInt(img.getAttribute(ATTRIBUTE_NATURAL_WIDTH))) *
                parseInt(img.getAttribute(ATTRIBUTE_NATURAL_HEIGHT))
        ).toString();
    }

    return placeholderDiv;
}
